import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Renderer2, RendererFactory2, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { addIcons } from 'ionicons';
import {
  alertCircleOutline,
  closeOutline,
  downloadOutline,
  openOutline,
} from 'ionicons/icons';
import { filter } from 'rxjs';

import { FileAssetPreviewService } from '@core/services/file-asset-preview.service';
import { MobileOverlayBackService } from '@core/services/mobile-overlay-back.service';

@Component({
  selector: 'app-mobile-file-preview-modal',
  standalone: true,
  imports: [
    CommonModule,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonModal,
    IonSpinner,
    IonTitle,
    IonToolbar,
    TranslatePipe,
  ],
  templateUrl: './mobile-file-preview-modal.component.html',
  styleUrls: ['./mobile-file-preview-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileFilePreviewModalComponent {
  protected readonly preview = inject(FileAssetPreviewService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlayBack = inject(MobileOverlayBackService);
  private readonly router = inject(Router);
  private readonly renderer: Renderer2;

  constructor() {
    const rendererFactory = inject(RendererFactory2);
    this.renderer = rendererFactory.createRenderer(null, null);

    addIcons({
      alertCircleOutline,
      closeOutline,
      downloadOutline,
      openOutline,
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.close());

    effect((onCleanup) => {
      if (!this.preview.previewModalVisible()) {
        return;
      }

      const unregister = this.overlayBack.register(() => this.close());
      onCleanup(() => unregister());
    });
  }

  close(): void {
    if (!this.preview.previewModalVisible()) {
      return;
    }

    this.preview.closePreview();
  }

  openExternal(): void {
    const url = this.preview.previewUrl();
    if (!url) {
      return;
    }

    this.clickHiddenAnchor(url, undefined, true);
  }

  download(): void {
    const entry = this.preview.previewEntry();
    const url = this.preview.previewUrl();
    if (!entry || !url) {
      return;
    }

    this.clickHiddenAnchor(url, entry.fileName, true);
  }

  retry(): void {
    this.preview.retryPreview();
  }

  private clickHiddenAnchor(url: string, downloadName?: string, newTab = false): void {
    const anchor = this.renderer.createElement('a') as HTMLAnchorElement;
    this.renderer.setAttribute(anchor, 'href', url);
    if (downloadName) {
      this.renderer.setAttribute(anchor, 'download', downloadName);
    }
    if (newTab) {
      this.renderer.setAttribute(anchor, 'target', '_blank');
      this.renderer.setAttribute(anchor, 'rel', 'noopener noreferrer');
    }
    this.renderer.setStyle(anchor, 'display', 'none');
    this.renderer.appendChild(this.document.body, anchor);
    anchor.click();
    this.renderer.removeChild(this.document.body, anchor);
  }
}